import { UserQuery, UsersQuery } from '@/graphql/generated/operations'
import { DeviceType } from '@/graphql/generated/schemas'
import {
  getArchivedLabel,
  removeUnderscoreAndCapitalize,
} from '@/utils/formats'

import {
  AccountGroupsIProps,
  AccountIProps,
  DEFAULT_ROLE,
  ROLE_OPTIONS,
  Role,
  RoleExtended,
  TechnicianFacilities,
} from '../types/types'

/**
 * The set of role string values that back the multi-select checkboxes
 * (InviteUserModal, ChangeRolesModal). Anything outside this set is not
 * a valid selection or `updateUserRoles` input.
 */
export const VALID_ROLE_VALUES = ROLE_OPTIONS.map((opt) => opt.value as string)

/**
 * Normalize a role string to a ROLE_OPTIONS value (i.e. what the
 * checkbox group and `updateUserRoles` accept). Unlike `normalizeRole`
 * (which targets RoleExtended and folds READ_ONLY_USER into unknown),
 * this preserves READ_ONLY_USER.
 */
export const normalizeToRoleOption = (role: string): Role | null => {
  if (!role) return null

  if (role === DEFAULT_ROLE) {
    return Role.OPERATOR
  }

  const upper = role.toUpperCase()
  const normalized = upper.startsWith('ROLE_')
    ? upper.replace('ROLE_', '')
    : upper

  return VALID_ROLE_VALUES.includes(normalized) ? (normalized as Role) : null
}

/**
 * Client-side exact-match lookup of a user id by email from a Users query
 * response. The backend filter is not exact, so we still need to disambiguate.
 */
export const findUserIdByEmail = (
  usersData: UsersQuery | undefined,
  email: string
): string | null => {
  const target = email.toLowerCase()
  const match = usersData?.users?.edges?.find(
    (edge) => edge?.node?.email?.toLowerCase() === target
  )
  return match?.node?.id ?? null
}

/**
 * Pull the ROLE_OPTIONS values out of a User query response. Falls back
 * to `roleName` for legacy users whose `roles` array is empty — matches
 * the pattern in ChangeRolesModal so a role update never silently drops
 * an existing legacy role.
 */
export const extractExistingRoles = (
  userData: UserQuery | undefined
): Role[] => {
  const user = userData?.user
  if (!user) return []

  const fromRolesArray = (user.roles ?? [])
    .map((r) => normalizeToRoleOption(r))
    .filter((r): r is Role => r !== null)
  if (fromRolesArray.length > 0) return fromRolesArray

  const fromRoleName = user.roleName
    ? normalizeToRoleOption(user.roleName)
    : null
  return fromRoleName ? [fromRoleName] : []
}

/**
 * Merge existing user roles with newly-selected roles into a deduped set.
 */
export const mergeRoles = (existing: Role[], selected: string[]): string[] =>
  Array.from(new Set<string>([...existing, ...selected]))

/**
 * Filter account based on full name or email of the user using filter phrase.
 */
export const filterAccounts = (
  accounts: AccountIProps[],
  filter: string
): AccountIProps[] =>
  accounts.filter((account) => {
    const lowerFilter = filter.toLocaleLowerCase()
    const fullName =
      `${account.firstName} ${account.lastName}`.toLocaleLowerCase()
    const email = account.email?.toLocaleLowerCase() || ''

    return (
      fullName.includes(lowerFilter) ||
      email.includes(lowerFilter) ||
      filter === ''
    )
  })

/**
 * Normalize a role string to match RoleExtended enum values.
 * Handles both "ROLE_OPERATOR" and "OPERATOR" formats.
 * Maps DEFAULT_ROLE to OPERATOR as per business logic.
 */
export const normalizeRole = (role: string): RoleExtended | null => {
  if (!role) return null

  // Handle DEFAULT_ROLE specially - it represents an operator
  if (role === DEFAULT_ROLE) {
    return RoleExtended.OPERATOR
  }

  const upper = role.toUpperCase()
  const normalized = upper.startsWith('ROLE_')
    ? upper.replace('ROLE_', '')
    : upper
  return normalized in RoleExtended ? (normalized as RoleExtended) : null
}

/**
 * Get every RoleExtended an account holds.
 * Prefers the roles array; falls back to roleName.
 */
const getAllRoles = (account: AccountIProps): RoleExtended[] => {
  if (account.roles && account.roles.length > 0) {
    const normalized = account.roles
      .map((r) => normalizeRole(r))
      .filter((r): r is RoleExtended => r !== null)
    if (normalized.length > 0) return normalized
  }
  if (account.roleName) {
    const normalized = normalizeRole(account.roleName)
    if (normalized) return [normalized]
  }
  return []
}

/**
 * Take account list and split it into groups according to assigned role.
 * Users with multiple roles appear in every group they belong to.
 */
export const groupAccounts = (
  accounts: AccountIProps[]
): AccountGroupsIProps => {
  const groupsMapInitial: { [key in RoleExtended]: AccountIProps[] } = {
    [RoleExtended.PROGRAM_MANAGER]: [],
    [RoleExtended.OPERATOR]: [],
    [RoleExtended.GUARD]: [],
    [RoleExtended.EMPLOYEE]: [],
    [RoleExtended.TECHNICIAN]: [],
    [RoleExtended.IMPLEMENTER]: [],
  }
  const groupsMap = accounts.reduce((groups, account) => {
    const roles = getAllRoles(account)

    if (roles.length > 0) {
      roles.forEach((role) => {
        groups[role].push({
          ...account,
          roleName: role,
        })
      })
    } else if (account.roleName === DEFAULT_ROLE) {
      // users with role 'default-roles-hivewarchdev.com' are in fact operators
      groups[RoleExtended.OPERATOR].push({
        ...account,
        roleName: RoleExtended.OPERATOR,
      })
    } else if (account.roleName === '' || !account.roleName) {
      groups[RoleExtended.EMPLOYEE].push(account)
    }
    return groups
  }, groupsMapInitial)
  return Object.entries(groupsMap).map(
    ([role, accounts]: [RoleExtended, AccountIProps[]]) => ({
      role,
      accounts,
    })
  )
}

export const getPascalCaseRole = (role: RoleExtended) => {
  switch (role) {
    case RoleExtended.PROGRAM_MANAGER:
      return 'ProgramManager'
    case RoleExtended.OPERATOR:
      return 'Operator'
    case RoleExtended.GUARD:
      return 'Guard'
    case RoleExtended.EMPLOYEE:
      return 'Employee'
    case RoleExtended.TECHNICIAN:
      return 'Technician'
    case RoleExtended.IMPLEMENTER:
      return 'Implementer'
    default:
      return ''
  }
}

export const getFacilitiesDisplayName = (facilities: TechnicianFacilities) => {
  return (
    (facilities?.map((f) => getArchivedLabel(f.name, f.archived)).join(', ') ??
      'n/a') ||
    'n/a'
  )
}

export const getDeviceTypeDisplayName = (deviceTypes: DeviceType[]) => {
  return (
    (deviceTypes?.map((d) => removeUnderscoreAndCapitalize(d)).join(', ') ??
      'n/a') ||
    'n/a'
  )
}
